// src/components/checklists/ChecklistGateDialog.js
// chklstweb-3a7f1c58 — "This job can't be completed yet" (web job pop-up).
//
// Opened by JobQuickViewModal when a fresh read says the job's checklist gate is
// 'blocked'. Lists each blocking checklist with how many required answers are
// missing, and a "Finish it" button that opens it with the missing answers outlined.
//
// Owner/Admin/Manager can "Complete anyway…" with a required reason (1–500 chars):
// overrideChecklistGate, then the normal completion. Everyone else is told who can.
// The override is recorded and shown on the job; the server refuses it for any
// other role (BUILDER_ROLES), whatever this dialog shows.
//
// LIVE: it reads the pop-up's live state. If the gate turns clear while it is open —
// usually a photo the server has just finished counting — it says so and offers
// Complete job, instead of making the person close and try again.

import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, X, Loader2 } from 'lucide-react';
import { blockingChecklists, OVERRIDE_REASON_MAX } from '../../utils/checklistClient';

const ChecklistGateDialog = ({
    isOpen, onClose, checklists, state, canOverride, isCompleting,
    onFinishIt,              // (checklistId)
    onOverrideAndComplete,   // async (reason) — throws with the server's message on refusal
    onCompleteNow,           // async () — the gate is clear now
}) => {
    const [step, setStep] = useState('list'); // list | reason
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) { setStep('list'); setReason(''); setBusy(false); setError(''); }
    }, [isOpen]);

    if (!isOpen) return null;

    const blocking = blockingChecklists(checklists);
    const nowClear = state?.checklistGate !== 'blocked' && !busy;

    const handleOverride = async () => {
        const trimmed = reason.trim();
        if (!trimmed) { setError('Please give a reason.'); return; }
        setBusy(true);
        setError('');
        try {
            await onOverrideAndComplete(trimmed);
        } catch (err) {
            setError(err?.message || 'That could not be saved. Try again.');
        } finally {
            setBusy(false);
        }
    };

    const working = busy || isCompleting;

    return (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
                    <div className="flex items-start gap-2">
                        {nowClear
                            ? <CheckCircle size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                            : <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />}
                        <h2 className="text-base font-bold text-[#12192D]">
                            {nowClear ? 'The checklists are finished now' : "This job can't be completed yet"}
                        </h2>
                    </div>
                    <button type="button" onClick={onClose} disabled={working} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                        <X size={16} />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    {nowClear ? (
                        <p className="text-sm text-slate-600">Nothing is holding this job back any more.</p>
                    ) : step === 'list' ? (
                        <>
                            <p className="text-sm text-slate-600">These checklists must be finished first:</p>
                            <ul className="space-y-2">
                                {blocking.map(c => (
                                    <li key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                                            <p className="text-xs text-red-600">
                                                {c.missing} required answer{c.missing === 1 ? '' : 's'} missing
                                            </p>
                                        </div>
                                        <button type="button" onClick={() => onFinishIt(c.id)}
                                            className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-[#5F4EC9] hover:bg-[#4f3fb5] rounded-lg">
                                            Finish it
                                        </button>
                                    </li>
                                ))}
                                {blocking.length === 0 && (
                                    <li className="text-xs text-slate-500">Checking the checklists… if you just added a photo, give it a few seconds.</li>
                                )}
                            </ul>
                            {!canOverride && (
                                <p className="text-xs text-slate-500">Only an Owner, Admin or Manager can complete a job without its checklist.</p>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-slate-600">
                                Say why this job is being completed without its checklist. Your name, the time and this reason are kept on the job.
                            </p>
                            <textarea
                                rows={3}
                                value={reason}
                                maxLength={OVERRIDE_REASON_MAX}
                                disabled={working}
                                onChange={e => { setReason(e.target.value); setError(''); }}
                                placeholder="e.g. Customer declined the meter photo; confirmed by phone."
                                className="w-full text-sm border border-slate-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30"
                            />
                            <p className="text-[10px] text-slate-400 text-right">{reason.length} / {OVERRIDE_REASON_MAX}</p>
                        </>
                    )}
                    {error && <p className="text-xs text-red-600">{error}</p>}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100">
                    {nowClear ? (
                        <>
                            <button type="button" onClick={onClose} disabled={working}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                                Not now
                            </button>
                            <button type="button" onClick={onCompleteNow} disabled={working}
                                className="px-4 py-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-60">
                                {isCompleting ? 'Completing…' : 'Complete job'}
                            </button>
                        </>
                    ) : step === 'list' ? (
                        <>
                            <button type="button" onClick={onClose}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                                Close
                            </button>
                            {canOverride && (
                                <button type="button" onClick={() => setStep('reason')}
                                    className="px-3 py-1.5 text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100">
                                    Complete anyway…
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <button type="button" onClick={() => { setStep('list'); setError(''); }} disabled={working}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                                Back
                            </button>
                            <button type="button" onClick={handleOverride} disabled={working || !reason.trim()}
                                className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-60">
                                {working && <Loader2 size={13} className="animate-spin" />}
                                {working ? 'Completing…' : 'Complete without checklist'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChecklistGateDialog;
